import { useCallback, useEffect, useRef, useState } from "react";
import { errorLogging } from "../config/utils";
import { RECAPTCHA_ACTION_LOGIN, RECAPTCHA_SITE_KEY } from "../config/constants";

export type RecaptchaAction = typeof RECAPTCHA_ACTION_LOGIN | string;

export function useReCaptcha(action: RecaptchaAction): {
  reCaptchaToken: string | undefined;
  refresh: () => void;
} {
  const [token, setToken] = useState<string | undefined>(undefined);
  const actionRef = useRef(action);

  type GrecaptchaEnterprise = {
    enterprise: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  };

  const execute = useCallback(() => {
    try {
      if (typeof window === "undefined") return;
      const w = window as Window & { grecaptcha?: GrecaptchaEnterprise };
      if (!w.grecaptcha || !w.grecaptcha.enterprise || typeof w.grecaptcha.enterprise.ready !== "function") {
        return;
      }
      w.grecaptcha.enterprise.ready(() => {
        try {
          w.grecaptcha!.enterprise
            .execute(RECAPTCHA_SITE_KEY, { action: actionRef.current })
            .then((tok: string) => {
              if (tok.length > 0) {
                setToken(tok);
              }
            })
            .catch((e: unknown) => {
              const err: string | object = typeof e === "string" ? e : (e ?? { message: "recaptcha_error" });
              errorLogging(err);
            });
        } catch (err) {
          const errObj: string | object = typeof err === "string" ? err : (err ?? { message: "recaptcha_error" });
          errorLogging(errObj);
        }
      });
    } catch (e) {
      const errObj: string | object = typeof e === "string" ? e : (e ?? { message: "recaptcha_error" });
      errorLogging(errObj);
    }
  }, []);

  useEffect(() => {
    actionRef.current = action;
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      execute();
      if ((window as Window & { grecaptcha?: GrecaptchaEnterprise }).grecaptcha || tries > 10) {
        window.clearInterval(timer);
      }
    }, 500);
    return () => window.clearInterval(timer);
  }, [action, execute]);

  const refresh = useCallback(() => {
    setToken(undefined);
    execute();
  }, [execute]);

  return { reCaptchaToken: token, refresh };
}


